import { SearchFocusTarget, User } from "@/App";
import { useCallback, useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { Header } from "./Header";
import { Footer } from "./Footer";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Shield,
  Users,
  FolderTree,
  Settings,
  Mail,
  MessageSquare,
  AlertTriangle,
  Globe,
  Loader2,
  Bug,
} from "lucide-react";
import { LanguageManagementPanel } from "./admin-dashboard-panels/LanguageManagementPanel";
import { UserManagementPanel } from "./admin-dashboard-panels/UserManagementPanel";
import { CategoryManagementPanel } from "./admin-dashboard-panels/CategoryManagementPanel";
import { BroadcastManagementPanel } from "./admin-dashboard-panels/BroadcastManagementPanel";
import { UserMessageManagementPanel } from "./admin-dashboard-panels/UserMessageManagementPanel";
import { TicketManagementPanel } from "./admin-dashboard-panels/TicketManagementPanel";
import { SettingsPanel } from "./admin-dashboard-panels/SettingsPanel";
import { SystemPanel } from "./admin-dashboard-panels/SystemPanel";
import { AdminSystemStatistics } from "@/types/adminSystem.types";
import { getSystemStatistics} from "@/services/adminSysten.service";
import { toast } from "sonner";
import type { BroadcastMessage } from "./BroadcastBanner";

interface AdminDashboardProps {
  navigate: (page: string) => void;
  currentUser: User | null;
  onLogout: () => void;
  onSearch: (query: string) => void;
  onScheduleBroadcast?: (broadcast: BroadcastMessage) => void;
  onNavigateToSearchPage: (target: SearchFocusTarget) => void;
}

export function AdminDashboard({
  navigate,
  currentUser,
  onLogout,
  onSearch,
  onScheduleBroadcast,
  onNavigateToSearchPage,
}: AdminDashboardProps) {
  const { t, language } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const mutedTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 50%, var(--surface))",
  } as const;
  const tabTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 82%, transparent)",
  } as const;
  const [stats, setStats] = useState<AdminSystemStatistics | null>(null);
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem("admin-tab") || "users";
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const scrollToTop = useCallback(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);
  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await getSystemStatistics();
        setStats(data);
      } catch (error) {
        console.error("Fehler beim Laden der Admin-Statistiken", error);
        toast.error("Statistiken konnten nicht geladen werden.");
      } finally {
        setLoadingStats(false);
      }
    };

    if (currentUser?.role === "admin") {
      loadStats();
    }
  }, [currentUser]);

  const handleScheduleBroadcast = (broadcast: BroadcastMessage) => {
    if (onScheduleBroadcast) {
      onScheduleBroadcast(broadcast);
    }
  };

  if (!currentUser || currentUser.role !== "admin") {
    navigate("home");
    return null;
  }

  const formatNumber = (num?: number) => num ? num.toLocaleString("de-DE") : "0";

  const renderValue = (value?: number) => {
    if (loadingStats) {
      return <Loader2 className="w-6 h-6 animate-spin" style={mutedTextStyle} />;
    }
    return <p className="text-primary-heading">{formatNumber(value)}</p>;
  };

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ backgroundColor: "var(--background)" }}
    >
      <Header
        navigate={navigate}
        currentUser={currentUser}
        onLogout={onLogout}
        currentPage="admin"
        samePageHandlers={{ admin: scrollToTop }}
        onNavigateToSearchPage={onNavigateToSearchPage}
      />

      <main className="flex-1 py-8">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-8 h-8 icon-primary" />
              <h2 className="text-primary-heading">{t("admin.title")}</h2>
            </div>
            <p className="text-secondary">{t("admin.subtitle")}</p>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="stat-card banner-info">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="icon-box">
                    <Users className="w-5 h-5 icon-primary" style={iconStyle} />
                  </div>
                  <div>
                    <p className="text-sm text-secondary">
                      {t("admin.totalUsers")}
                    </p>
                    {renderValue(stats?.totalUsers)}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card banner-info">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="icon-box">
                    <FolderTree className="w-5 h-5 icon-primary" style={iconStyle} />
                  </div>
                  <div>
                    <p className="text-sm text-secondary">
                      {t("categories.title")}
                    </p>
                    {renderValue(stats?.totalCategories)}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card banner-info">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="icon-box">
                    <Shield className="w-5 h-5 icon-primary" style={iconStyle} />
                  </div>
                  <div>
                    <p className="text-sm text-secondary">
                      {t("admin.administrators")}
                    </p>
                    {renderValue(stats?.totalAdmins)}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card banner-info">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="icon-box">
                    <Mail className="w-5 h-5 icon-primary" style={iconStyle} />
                  </div>
                  <div>
                    <p className="text-sm text-secondary">
                      {t("admin.pendingSuggestions")}
                    </p>
                    {renderValue(stats?.openSuggestions)}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <Tabs value={activeTab} onValueChange={(val) => {
            setActiveTab(val);
            sessionStorage.setItem("admin-tab", val);
          }} className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-3 lg:grid-cols-8 tabs-list-standard h-auto gap-2 p-2">
              <TabsTrigger
                value="users"
                className="gap-1 md:gap-2 tabs-trigger-standard"
                style={tabTextStyle}
              >
                <Users className="w-4 h-4" style={iconStyle} />
                <span className="hidden sm:inline">{t("admin.users")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="categories"
                className="gap-1 md:gap-2 tabs-trigger-standard"
                style={tabTextStyle}
              >
                <FolderTree className="w-4 h-4" style={iconStyle} />
                <span className="hidden sm:inline">
                  {t("admin.categories")}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="broadcast"
                className="gap-1 md:gap-2 tabs-trigger-standard"
                style={tabTextStyle}
              >
                <MessageSquare className="w-4 h-4" style={iconStyle} />
                <span className="hidden sm:inline">{t("admin.broadcast")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="messages"
                className="gap-1 md:gap-2 tabs-trigger-standard"
                style={tabTextStyle}
              >
                <Mail className="w-4 h-4" style={iconStyle} />
                <span className="hidden sm:inline">{t("admin.messages")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="tickets"
                className="gap-1 md:gap-2 tabs-trigger-standard"
                style={tabTextStyle}
              >
                <Bug className="w-4 h-4" style={iconStyle} />
                <span className="hidden sm:inline">{t("admin.tickets")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="languages"
                className="gap-1 md:gap-2 tabs-trigger-standard"
                style={tabTextStyle}
              >
                <Globe className="w-4 h-4" style={iconStyle} />
                <span className="hidden sm:inline">{t("admin.languages")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="settings"
                className="gap-1 md:gap-2 tabs-trigger-standard"
                style={tabTextStyle}
              >
                <Settings className="w-4 h-4" style={iconStyle} />
                <span className="hidden sm:inline">{t("admin.settings")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="system"
                className="gap-1 md:gap-2 tabs-trigger-standard"
                style={tabTextStyle}
              >
                <AlertTriangle className="w-4 h-4" style={iconStyle} />
                <span className="hidden sm:inline">{t("admin.system")}</span>
              </TabsTrigger>
            </TabsList>

            {/* Users Tab */}
            <TabsContent value="users" className="mt-8 md:mt-6">
              <UserManagementPanel />
            </TabsContent>

            {/* Categories Tab */}
            <TabsContent value="categories" className="mt-8 md:mt-6">
              <CategoryManagementPanel />
            </TabsContent>

            {/* Broadcast Tab */}
            <TabsContent value="broadcast" className="mt-8 md:mt-6">
              <BroadcastManagementPanel
                onScheduleBroadcast={handleScheduleBroadcast}
              />
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages" className="mt-8 md:mt-6">
              <UserMessageManagementPanel />
            </TabsContent>

            <TabsContent value="tickets" className="mt-8 md:mt-6">
              <TicketManagementPanel />
            </TabsContent>

            {/* Languages Tab */}
              <TabsContent value="languages" className="mt-8 md:mt-6">
                <Card className="stat-card">
                <CardHeader>
                  <CardTitle
                    className="card-title-text flex items-center gap-2"
                    style={primaryTextStyle}
                  >
                    <Globe className="w-5 h-5" style={iconStyle} />
                    {t("admin.languageManagement")}
                  </CardTitle>
                  <CardDescription
                    className="card-description-text"
                    style={secondaryTextStyle}
                  >
                    {t("admin.languageManagementDescription")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <LanguageManagementPanel />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="mt-8 md:mt-6">
              <SettingsPanel />
            </TabsContent>

            {/* System Tab */}
            <TabsContent value="system" className="mt-8 md:mt-6">
              <SystemPanel navigate={navigate} />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer navigate={navigate} />
    </div>
  );
}
